// Import the functions you need from the SDKs you need
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAnalytics, Analytics, isSupported, logEvent } from 'firebase/analytics';
import { Metric } from 'web-vitals';

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'your-api-key',
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'your-project.firebaseapp.com',
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'your-project-id',
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'your-project.appspot.com',
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '123456789',
    appId: process.env.REACT_APP_FIREBASE_APP_ID || 'your-app-id',
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || 'G-XXXXXXXXXX',
};

// Initialize Firebase
let app: FirebaseApp;
let analytics: Analytics | null = null;
let pendingErrors: string[] = [];
let pendingMetrics: Metric[] = [];

try {
    app = initializeApp(firebaseConfig);

    // Initialize Analytics (only in browser, if supported, and in production)
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
        isSupported()
            .then(supported => {
                if (supported) {
                    analytics = getAnalytics(app);
                    // Report any pending errors now that analytics is ready
                    reportPendingErrors();
                } else {
                    pendingErrors.push('analytics_not_supported');
                }
            })
            .catch(() => {
                pendingErrors.push('analytics_support_check_failed');
            });
    }
} catch (error) {
    pendingErrors.push('firebase_initialization_failed');
}

// Function to report pending initialization errors
const reportPendingErrors = () => {
    if (analytics && pendingErrors.length > 0) {
        pendingErrors.forEach(errorType => {
            try {
                if (analytics) {
                    logEvent(analytics, 'analytics_error', {
                        error_type: errorType,
                    });
                }
            } catch (e) {
                // Final silent fallback
            }
        });
        pendingErrors = [];
    }
    // Report any pending web vitals metrics
    if (analytics && pendingMetrics.length > 0) {
        pendingMetrics.forEach(metric => {
            try {
                if (analytics) {
                    logEvent(analytics, 'web_vitals', {
                        name: metric.name,
                        value: metric.value,
                        delta: metric.delta,
                        id: metric.id,
                        entries: JSON.stringify(metric.entries), // Serialize entries for logging
                    });
                }
            } catch (e) {
                // Silent fallback
            }
        });
        pendingMetrics = [];
    }
};

export { app, analytics };

// Function to send web vitals metrics to Firebase Analytics
export const sendWebVitals = (metric: Metric) => {
    if (analytics) {
        logEvent(analytics, 'web_vitals', {
            name: metric.name,
            value: metric.value,
            delta: metric.delta,
            id: metric.id,
            entries: JSON.stringify(metric.entries),
        });
    } else {
        pendingMetrics.push(metric);
    }
};
